/**
 * The Rail — ONE surface card, ONE `cardline` border, sections separated by
 * hairlines and nothing nested inside it (fix D / R1 + R6.1):
 *
 *   RailActions   the icon row: Lobby · New game · Analysis · Review|Tournament
 *   RailTabs      Moves · Info · [Standings · Pairings · Leaderboard]
 *   body          the active tab, flex 1 / minHeight 0
 *   MoveNav       ⏮ ◀ ▶ ⏭, pinned under the body on EVERY tab
 *   ActionBar     ½ Draw · ⚑ Resign … | 🔊 🎨   (the phone keeps M1b's Toolbar)
 *
 * UX spec §1 rule 3 / §6 rule 2 still holds: exactly one body is mounted at a
 * time and the bodies that are not the current state do NOT exist in the tree,
 * so a socket message cannot re-render them. The body swap is the 180 ms
 * cross-fade + 8 px slide of UX spec §7.
 *
 * The analysis machinery is mounted ONLY while `analysisOn` — `EngineHost` is
 * what owns the Stockfish worker, so a live game has no engine in the tree at
 * all (R4's fair-play rule, asserted in `__tests__/analysis.test.ts`).
 *
 * Fix K: the nav row and the action bar / toolbar ALSO need `showMoveNav` /
 * `showActionBar` on top of `live` — the idle tournament wait (§9) is a
 * `live` body (the tab strip and the icon row belong there) with no game of
 * mine, and no watch, for either of them to act on.
 *
 * Fix S / S6: `FloatingDrawOffer` is a SIBLING of the body, inside its own
 * small `bodyWrap` (so it is positioned against the body's own box, not the
 * whole Rail card, and never covers `RailActions`/`RailTabs`) — a draw
 * offer floats over whichever panel is open while the rail is off Moves.
 */
import React, {Suspense, useEffect, useRef} from 'react';
import {Animated, Easing, Platform, StyleSheet, View} from 'react-native';

import {RailState} from '@/Store/liveGame/selectors';

import EngineHost from '../analysis/EngineHost';
import RailActions from '../actions/RailActions';
import NoticeCard from '../NoticeCard';
import FloatingDrawOffer from '../live/FloatingDrawOffer';
import MoveNav from '../live/MoveNav';
import ActionBar from '../live/ActionBar';
import RailTabs from '../tabs/RailTabs';
import TabBody from '../tabs/TabBody';
import {MOTION, RADIUS, SPACE} from '../../theme/tokens';
import {useV2Theme} from '../../theme/useV2Theme';
import {RailModel} from './model';

const MatchedCard = React.lazy(() => import('../matched/MatchedCard'));
const Toolbar = React.lazy(() => import('../live/Toolbar'));

/** the body swap's slide (UX spec §7) */
const RISE_FROM = 8;

export type RailViewProps = RailModel & {railState: RailState};

export default React.memo(function RailView({
  railState,
  body,
  analysisOn,
  phone,
  showActionBar,
  showMoveNav,
}: RailViewProps) {
  const tokens = useV2Theme(s => s.tokens);
  const fade = useRef(new Animated.Value(1)).current;
  const rise = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    fade.setValue(0);
    rise.setValue(RISE_FROM);
    Animated.parallel([
      Animated.timing(fade, {
        toValue: 1,
        duration: MOTION.ms,
        easing: Easing.bezier(0.2, 0.8, 0.3, 1),
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(rise, {
        toValue: 0,
        duration: MOTION.ms,
        easing: Easing.bezier(0.2, 0.8, 0.3, 1),
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();
  }, [railState, fade, rise]);

  const live = body === 'live';

  return (
    <View
      style={[
        styles.rail,
        {
          backgroundColor: tokens.surface,
          borderColor: tokens.cardline,
          boxShadow: tokens.sh1,
        } as any,
      ]}>
      {live && <RailActions />}
      {live && <RailTabs />}
      {railState === 'evalPending' && (
        <View style={styles.notice}>
          <NoticeCard
            title="Engine is evaluating"
            body="The opening report runs after 20 moves. The board comes back as soon as it is done."
          />
        </View>
      )}
      <View style={styles.bodyWrap}>
        <Animated.View
          style={[
            styles.body,
            {opacity: fade, transform: [{translateY: rise}]},
          ]}>
          <Suspense fallback={<View style={styles.body} />}>
            {live && <TabBody />}
            {body === 'matched' && <MatchedCard />}
          </Suspense>
        </Animated.View>
        {/* S6: floats over whichever panel is open, never on Moves — the
            Moves tab keeps its own EventDock copy of the same card. */}
        {live && <FloatingDrawOffer />}
      </View>
      {live && showMoveNav && <MoveNav />}
      {live &&
        showActionBar &&
        (phone ? (
          <Suspense fallback={null}>
            <Toolbar />
          </Suspense>
        ) : (
          <ActionBar />
        ))}
      {analysisOn && <EngineHost />}
    </View>
  );
});

const styles = StyleSheet.create({
  rail: {
    flex: 1,
    minWidth: 0,
    // R6.1: 12 px, the same radius as the board frame and the player rows
    borderRadius: RADIUS.panel,
    borderWidth: 1,
    overflow: 'hidden',
  },
  bodyWrap: {
    flex: 1,
    minHeight: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    paddingVertical: SPACE.md,
    paddingHorizontal: SPACE.lg,
  },
  notice: {
    paddingTop: SPACE.md,
    paddingHorizontal: SPACE.lg,
  },
});
